import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  SCORE_DIMENSIONS,
  letterIndex,
  type ScoreLetter,
  type DimLang,
  type Horizon,
} from '../../shared/scoreDimensions';
import type { CardCategory } from '../types';
import { useT } from '../i18n';

export interface MatrixPick {
  topic_id: number;
  name: string;
  category_color?: string | null;
  context?: string | null;
  horizon?: Horizon | null;
}

export interface MatrixSession {
  session_id: number;
  scheduled_date: string;
  title?: string | null;
  picks: MatrixPick[];
  /** Marks the column rendered with the accent (the session currently open).
   *  The header only says "aujourd'hui" when that session's date really is
   *  today — a past session being viewed shows its actual date. */
  isToday?: boolean;
}

export type TrendScores = Record<(typeof SCORE_DIMENSIONS)[number]['key'], ScoreLetter | null> & {
  takes_cards?: boolean | null;
};

interface Props {
  /** Sessions in chronological order (oldest → newest). */
  sessions: MatrixSession[];
  /** Observation letters per session, same order and length as `sessions`.
   *  Omit to render the cards part only. */
  series?: TrendScores[];
  /** The deck's categories, to order the colour rows and name the colours never taken. */
  categories?: CardCategory[];
  /** Per-card synthesis of the evolution (LLM), by topic id — shown in the
   *  last column in place of the latest context when present. */
  syntheses?: Record<number, string>;
}

function formatShortDate(iso: string, lang: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(lang === 'fr' ? 'fr-CH' : 'en-CH', {
    day: 'numeric', month: 'short', year: '2-digit',
  });
}

function localIsoToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Archived cards (old-deck, no category) get a neutral dot — never the accent,
 *  which would read as some category's color. */
const NO_CATEGORY_DOT = 'var(--ink-3)';

const HORIZON_LETTER: Record<Horizon, string> = { court: 'C', moyen: 'M', long: 'L' };
const LETTERS: ScoreLetter[] = ['A', 'B', 'C', 'D', 'E'];

/** Chart geometry: five levels A–E over 150px; each dimension's line is
 *  nudged by a few px so coinciding letters stay distinguishable. */
const CHART_H = 150;
const CHART_PAD = 12;
const NUDGE = [-4, -2, 0, 2, 4];
const yFor = (level: number) => CHART_H - CHART_PAD - (level / 4) * (CHART_H - 2 * CHART_PAD);

/**
 * Text clamped to a few lines; when it is really cut, a "plus" link appears
 * and opens that cell to its full height (the row grows, the grid stays).
 */
function ClampText({ text, lines, synth }: { text: string; lines: number; synth?: boolean }) {
  const { t } = useT();
  const ref = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [cut, setCut] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setCut(!open && el.scrollHeight > el.clientHeight + 1);
  }, [text, open, lines]);
  useEffect(() => {
    const onResize = () => { const el = ref.current; if (el && !open) setCut(el.scrollHeight > el.clientHeight + 1); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open]);
  useEffect(() => { setOpen(false); }, [text]);
  return (
    <>
      <span
        ref={ref}
        className={`evo-context-text ${synth ? 'evo-context-text--synth' : ''} ${open ? 'evo-context-text--open' : ''}`}
        style={open ? undefined : { WebkitLineClamp: lines }}
        onClick={() => { if (cut || open) setOpen((v) => !v); }}
      >
        {text}
      </span>
      {(cut || open) && (
        <button type="button" className="evo-more" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {open ? t('common.showLess') : t('common.showMore')}
        </button>
      )}
    </>
  );
}

/** A small card face: white, the category dot bottom-left, the horizon letter bottom-right. */
function MiniCard({ color, horizon, title }: { color: string | null; horizon?: Horizon | null; title?: string }) {
  return (
    <span className="history-mini-card" title={title}>
      <span className="history-mini-card-dot" style={{ background: color ?? NO_CATEGORY_DOT }} />
      {horizon && <span className="history-mini-card-h">{HORIZON_LETTER[horizon]}</span>}
    </span>
  );
}

/**
 * The participant's evolution as ONE time-aligned table.
 *
 * One row per distinct card, GROUPED BY COLOUR (category): the category name
 * sits in the label column, each chosen séance shows a small card face
 * (horizon letter C/M/L when set), and the last column spells the card's
 * evolution out — the LLM synthesis when there is one, else the latest
 * context written for it. Under the cards, "Couleurs": one row per colour
 * with as many card faces as taken that séance, and the colours never taken.
 * Below, the observations as ONE chart: the four dimensions are four coloured
 * lines across the séance columns (points placed on the measured column
 * centres, so they align with the cards above), legend with first → last.
 */
export function EvolutionTable({ sessions, series, categories, syntheses }: Props) {
  const { t, lang } = useT();
  const dl = lang as DimLang;
  const todayIso = localIsoToday();

  const groups = useMemo(() => {
    const byTopic = new Map<number, {
      name: string; color: string | null; count: number; firstIdx: number;
      lastContext: string | null; lastContextIdx: number;
    }>();
    sessions.forEach((s, idx) => {
      for (const p of s.picks) {
        const ex = byTopic.get(p.topic_id);
        if (ex) {
          ex.count++;
          ex.name = p.name;
          if (!ex.color && p.category_color) ex.color = p.category_color;
          if (p.context && p.context.trim()) { ex.lastContext = p.context.trim(); ex.lastContextIdx = idx; }
        } else {
          byTopic.set(p.topic_id, {
            name: p.name,
            color: p.category_color ?? null,
            count: 1,
            firstIdx: idx,
            lastContext: p.context && p.context.trim() ? p.context.trim() : null,
            lastContextIdx: idx,
          });
        }
      }
    });
    const rows = Array.from(byTopic.entries()).map(([topic_id, v]) => ({
      topic_id,
      ...v,
      cells: sessions.map((s) => s.picks.find((p) => p.topic_id === topic_id) ?? null),
    }));
    // group by colour; categories in deck order, uncategorised (old deck) last
    const byColor = new Map<string, typeof rows>();
    for (const r of rows) {
      const k = r.color ?? '';
      byColor.set(k, [...(byColor.get(k) ?? []), r]);
    }
    const order = (categories ?? []).map((c) => c.color);
    return Array.from(byColor.entries())
      .map(([color, list]) => ({
        color: color || null,
        name: color ? (categories?.find((c) => c.color === color)?.name ?? '') : t('evo.oldDeck'),
        total: list.reduce((n, r) => n + r.count, 0),
        rows: list.sort((a, b) => b.count - a.count || a.firstIdx - b.firstIdx),
        // cards of this colour taken per séance
        counts: sessions.map((s) => s.picks.filter((p) => (p.category_color ?? null) === (color || null)).length),
      }))
      .sort((a, b) => {
        const ia = a.color ? order.indexOf(a.color) : 999, ib = b.color ? order.indexOf(b.color) : 999;
        return (ia === -1 ? 998 : ia) - (ib === -1 ? 998 : ib);
      });
  }, [sessions, categories, t]);

  const neverTaken = useMemo(() => {
    if (!categories) return [];
    const used = new Set(sessions.flatMap((s) => s.picks.map((p) => p.category_color)));
    return categories.filter((c) => !used.has(c.color));
  }, [sessions, categories]);

  const hasSeries = !!series && series.length === sessions.length;
  const hasAnyPick = groups.length > 0;

  // ── Chart geometry: measured from the header cells so the points sit on
  //    the column centres whatever the widths. ──
  const wrapRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HTMLTableCellElement>(null);
  const headRefs = useRef<(HTMLTableCellElement | null)[]>([]);
  const [geom, setGeom] = useState<{ width: number; cols: { left: number; width: number }[] } | null>(null);
  useLayoutEffect(() => {
    if (!hasSeries) return;
    const measure = () => {
      const td = chartRef.current;
      if (!td) return;
      const r = td.getBoundingClientRect();
      const cols = headRefs.current.slice(0, sessions.length).map((th) => {
        if (!th) return { left: 0, width: 0 };
        const b = th.getBoundingClientRect();
        return { left: b.left - r.left, width: b.width };
      });
      const next = { width: Math.round(r.width), cols: cols.map((c) => ({ left: Math.round(c.left), width: Math.round(c.width) })) };
      setGeom((prev) => (prev && JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro && wrapRef.current) ro.observe(wrapRef.current);
    return () => { ro?.disconnect(); };
  }, [sessions.length, hasSeries]);

  if (sessions.length === 0 || (!hasAnyPick && !hasSeries)) {
    return <div className="empty-state">{t('history.empty')}</div>;
  }

  const colSpan = sessions.length + 2;
  const trendWord = (a: number, b: number) => (b > a ? t('evo.more') : b < a ? t('evo.less') : t('evo.same'));
  const dir = (a: number, b: number) => (b > a ? 'up' : b < a ? 'down' : 'flat');

  // Observation lines: level per séance (null = not recorded), per dimension.
  const dims = hasSeries
    ? SCORE_DIMENSIONS.map((dim, k) => {
        const values = sessions.map((_, i) => { const v = series![i][dim.key] ?? null; return v ? letterIndex(v) : null; });
        const nonNull = values.filter((v): v is number => v != null);
        return { dim, k, values, first: nonNull[0] ?? null, last: nonNull.length ? nonNull[nonNull.length - 1] : null, n: nonNull.length };
      })
    : [];
  const centerX = (i: number) => (geom ? geom.cols[i].left + geom.cols[i].width / 2 : 0);

  return (
    <div className="history-grid-wrap" ref={wrapRef}>
      <table className="history-grid">
        <thead>
          <tr>
            <th className="history-grid-corner"></th>
            {sessions.map((s, i) => (
              <th
                key={s.session_id}
                ref={(el) => { headRefs.current[i] = el; }}
                className={`history-grid-col ${s.isToday ? 'history-grid-col--today' : ''}`}
                title={s.title ?? ''}
              >
                {s.isToday && s.scheduled_date === todayIso
                  ? t('upcoming.today')
                  : formatShortDate(s.scheduled_date, lang)}
              </th>
            ))}
            <th className="history-grid-evo">{t('evo.delta')}</th>
          </tr>
        </thead>
        <tbody>
          {/* ── Cards, grouped by colour ── */}
          {groups.map((g) => (
            <Fragment key={`g-${g.color ?? 'none'}`}>
              <tr className="history-grid-group">
                <td className="history-grid-row-label history-grid-group-label">
                  <span className="history-grid-row-dot" style={{ background: g.color ?? NO_CATEGORY_DOT }} />
                  {g.name} <span className="history-grid-row-count">{g.total}×</span>
                </td>
                {sessions.map((s) => (
                  <td key={s.session_id} className={`history-grid-cell ${s.isToday ? 'history-grid-cell--today' : ''}`} />
                ))}
                <td className="history-grid-evo" />
              </tr>
              {g.rows.map((r) => {
                const synthesis = syntheses?.[r.topic_id];
                return (
                  <tr key={r.topic_id}>
                    <td className="history-grid-row-label history-grid-card-label" title={r.name}>
                      {r.name}
                      {r.count > 1 && <span className="history-grid-row-count">{r.count}×</span>}
                    </td>
                    {r.cells.map((pick, i) => (
                      <td key={i} className={`history-grid-cell ${sessions[i].isToday ? 'history-grid-cell--today' : ''}`}>
                        {pick && (
                          <MiniCard color={r.color} horizon={pick.horizon} title={pick.context ? `${r.name} — ${pick.context}` : r.name} />
                        )}
                      </td>
                    ))}
                    <td className="history-grid-evo evo-context">
                      {synthesis
                        ? <ClampText text={synthesis} lines={3} synth />
                        : r.lastContext
                          ? <>
                              <span className="evo-context-date">{formatShortDate(sessions[r.lastContextIdx].scheduled_date, lang)}</span>
                              <ClampText text={r.lastContext} lines={2} />
                            </>
                          : <span className="evo-context-none">{t('evo.noContext')}</span>}
                    </td>
                  </tr>
                );
              })}
            </Fragment>
          ))}

          {/* ── Colours: one row per colour, as many card faces as taken that séance ── */}
          {hasAnyPick && (
            <>
              <tr className="history-grid-divider">
                <td colSpan={colSpan}>{t('evo.colours')}</td>
              </tr>
              {groups.map((g) => {
                const first = g.counts[0], last = g.counts[g.counts.length - 1];
                return (
                  <tr key={`c-${g.color ?? 'none'}`} className="history-grid-colour">
                    <td className="history-grid-row-label" title={g.name}>
                      <span className="history-grid-row-dot" style={{ background: g.color ?? NO_CATEGORY_DOT }} />
                      {g.name}
                    </td>
                    {g.counts.map((n, i) => (
                      <td key={i} className={`history-grid-cell ${sessions[i].isToday ? 'history-grid-cell--today' : ''}`}>
                        {n > 0 && (
                          <span className="history-stack" title={`${n} ${n > 1 ? t('fds.cards') : t('fds.card')}`}>
                            {Array.from({ length: Math.min(n, 3) }, (_, j) => <MiniCard key={j} color={g.color} />)}
                            {n > 3 && <span className="history-stack-more">+{n - 3}</span>}
                          </span>
                        )}
                      </td>
                    ))}
                    <td className="history-grid-evo">
                      {sessions.length > 1 && (
                        <>
                          <span className={`evo-delta-letters evo-delta-letters--${dir(first, last)}`}>{first} → {last}</span>
                          <span className="evo-delta-word">{trendWord(first, last)}</span>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
              {neverTaken.length > 0 && (
                <tr className="history-grid-colour history-grid-never">
                  <td className="history-grid-row-label">{t('evo.neverTaken')}</td>
                  {sessions.map((s) => (
                    <td key={s.session_id} className={`history-grid-cell ${s.isToday ? 'history-grid-cell--today' : ''}`} />
                  ))}
                  <td className="history-grid-evo" title={neverTaken.map((c) => c.name).join(', ')}>
                    {neverTaken.map((c) => (
                      <i key={c.id} className="never-dot" style={{ borderColor: c.color }} title={c.name} />
                    ))}
                  </td>
                </tr>
              )}
            </>
          )}

          {/* ── Observations: the four dimensions as four lines in one chart ── */}
          {hasSeries && (
            <>
              <tr className="history-grid-divider">
                <td colSpan={colSpan}>{t('evo.observations')}</td>
              </tr>
              <tr className="evo-chart-row">
                <td className="history-grid-row-label evo-chart-label">
                  {t('evo.dimensions')}
                  <span className="evo-axis" aria-hidden="true">
                    {LETTERS.map((L, lv) => <span key={L} style={{ top: yFor(lv) }}>{L}</span>)}
                  </span>
                </td>
                <td className="evo-chart-cell" colSpan={sessions.length} ref={chartRef}>
                  {geom && (
                    <svg
                      className="evo-chart-svg"
                      viewBox={`0 0 ${geom.width} ${CHART_H}`}
                      width="100%"
                      height={CHART_H}
                      role="img"
                      aria-label={t('evo.dimensions')}
                    >
                      {sessions.map((s, i) => s.isToday && (
                        <rect key={`t-${i}`} className="evo-chart-today" x={geom.cols[i].left} y={0} width={geom.cols[i].width} height={CHART_H} />
                      ))}
                      {LETTERS.map((L, lv) => (
                        <line key={`h-${L}`} className="evo-chart-grid" x1={0} x2={geom.width} y1={yFor(lv)} y2={yFor(lv)} />
                      ))}
                      {geom.cols.slice(1).map((c, i) => (
                        <line key={`v-${i}`} className="evo-chart-grid" x1={c.left} x2={c.left} y1={0} y2={CHART_H} />
                      ))}
                      {dims.map(({ k, values }) => {
                        const pts = values.map((v, i) => (v == null ? null : { x: centerX(i), y: yFor(v) + NUDGE[k], i }))
                          .filter((p): p is { x: number; y: number; i: number } => p != null);
                        if (pts.length === 0) return null;
                        return (
                          <g key={k} className={`evo-line evo-line--${k}`}>
                            {pts.length > 1 && (
                              <polyline points={pts.map((p) => `${p.x},${p.y}`).join(' ')} fill="none" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                            )}
                            {pts.map((p) => (
                              <circle
                                key={p.i}
                                cx={p.x}
                                cy={p.y}
                                r={sessions[p.i].isToday ? 4.5 : 3}
                                className={sessions[p.i].isToday ? 'evo-point evo-point--today' : 'evo-point'}
                                strokeWidth={2}
                              >
                                <title>{`${SCORE_DIMENSIONS[k].label[dl]} — ${LETTERS[values[p.i]!]} · ${SCORE_DIMENSIONS[k].levels[LETTERS[values[p.i]!]].short[dl]}`}</title>
                              </circle>
                            ))}
                          </g>
                        );
                      })}
                    </svg>
                  )}
                </td>
                <td className="history-grid-evo evo-legend">
                  {dims.map(({ dim, k, first, last, n }) => (
                    <div key={dim.key} className="evo-legend-row" title={dim.help[dl]}>
                      <i className={`evo-legend-swatch evo-line--${k}`} />
                      <span className="evo-legend-name">{dim.label[dl]}</span>
                      {first != null && last != null ? (
                        <span className={`evo-delta-letters evo-delta-letters--${n > 1 ? dir(first, last) : 'flat'}`} title={dim.levels[LETTERS[last]].short[dl]}>
                          {n > 1 ? `${LETTERS[first]} → ${LETTERS[last]}` : LETTERS[last]}
                        </span>
                      ) : (
                        <span className="evo-delta-letters">—</span>
                      )}
                    </div>
                  ))}
                </td>
              </tr>
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}
